import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NotificationService } from '../../shared/services/notification.service';
import { environment } from '@bling-fe/shared/env';
import {
  HttpErrorHandlerStrategy,
  matchesStatus
} from './http-error-handler-strategy';
import { marker as _ } from '@bling-fe/shared/utils';
import {
  BadRequest,
  Forbidden,
  InternalServerError,
  NoResponse,
  NotFound,
  Offline,
  ServiceUnavailable,
  Unauthenticated,
  Unknown,
  UnprocessableEntity
} from './customized-errors';
import { captureException } from '@sentry/angular';

export const isOffline = (): boolean => !navigator.onLine;

export const isServerOrNetworkFailure = (
  error: unknown
): error is HttpErrorResponse =>
  error instanceof HttpErrorResponse &&
  (error.status === 0 || error.status >= 500);

// init_transaction guards reject with a machine `code` so the user is told
// why the payment cannot start instead of a generic 422 toast. These are
// expected, user-recoverable states, so they are surfaced as a message only
// and not reported to Sentry.
const PAYMENT_GUARD_MESSAGES: Record<string, string> = {
  already_paid: _('ERROR.PAYMENT.ALREADY_PAID'),
  not_awaiting_payment: _('ERROR.PAYMENT.NOT_AWAITING_PAYMENT')
};

const paymentGuardMessage = (e: HttpErrorResponse): string | undefined => {
  const code = (e.error as { error?: { code?: string } })?.error?.code;
  return code ? PAYMENT_GUARD_MESSAGES[code] : undefined;
};

type HandledHttpErrorTypes =
  | 'bad_request'
  | 'internal_server_error'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'service_unavailable'
  | 'no_response'
  | 'offline'
  | 'unprocessable_entity'
  | 'unknown';

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {
  private httpErrorHandlerStrategies: Array<HttpErrorHandlerStrategy> = [];
  private sessionErrorsHandledByInterceptor = new WeakSet<HttpErrorResponse>();

  constructor(private notificationService: NotificationService) {
    this.httpErrorHandlerStrategies.push(
      {
        matches: e => matchesStatus(e, 400),
        handle: e => this.errorHandler('bad_request', e.url)
      },
      // Payment-guard 422s (already_paid / not_awaiting_payment) get a
      // specific, translated message; must precede the generic 422 below.
      {
        matches: e => matchesStatus(e, 422) && !!paymentGuardMessage(e),
        handle: e => this.notificationService.error(paymentGuardMessage(e))
      },
      // example case, when user create new repair message
      // but there aren't any Rental objects
      {
        matches: e => matchesStatus(e, 422),
        handle: e => this.errorHandler('unprocessable_entity', e.url)
      },
      {
        matches: e => matchesStatus(e, 500),
        handle: e => this.errorHandler('internal_server_error', e.url)
      },
      {
        matches: e => matchesStatus(e, 401),
        handle: e => this.errorHandler('unauthenticated', e.url)
      },
      {
        matches: e => matchesStatus(e, 403),
        handle: e => {
          this.errorHandler('forbidden', e.url);
        }
      },
      {
        matches: e => matchesStatus(e, 404),
        handle: e => this.errorHandler('not_found', e.url)
      },
      {
        matches: e => matchesStatus(e, 504),
        handle: e => this.errorHandler('service_unavailable', e.url)
      },
      {
        matches: e => matchesStatus(e, 0),
        handle: e => this.errorHandler('no_response', e.url)
      },
      {
        matches: __ => isOffline(),
        handle: e => this.errorHandler('offline', e.url)
      }
    );
  }

  handleError(error) {
    // eslint-disable-next-line no-console
    console.error('handleError:', error);
    this.notificationService.error(_('ERROR.GLOBAL.UNKNOWN'));
  }

  // Non-http failures that are really connectivity problems (e.g. a lazy
  // chunk failing while offline) reuse the same classification and toast as
  // the http offline strategy.
  handleOfflineError(requestedUrl: string) {
    this.errorHandler('offline', requestedUrl);
  }

  handleHttpError(httpErrorResponse: HttpErrorResponse) {
    if (!(httpErrorResponse instanceof HttpErrorResponse)) {
      throw new Error(_('ERROR.GLOBAL.NOT_HTTP_ERROR_RESPONSE'));
    }
    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.log('handleHttpError:', httpErrorResponse);
    }
    if (this.sessionErrorsHandledByInterceptor.has(httpErrorResponse)) {
      return;
    }
    const errorHandlingStrategy = this.httpErrorHandlerStrategies.find(s =>
      s.matches(httpErrorResponse)
    );

    if (errorHandlingStrategy) {
      errorHandlingStrategy.handle(httpErrorResponse);
    } else {
      this.errorHandler('unknown', httpErrorResponse.url);
    }
  }

  reportLostSession(requestedUrl: string) {
    this.errorHandler('unauthenticated', requestedUrl);
  }

  acknowledgeSessionError(httpErrorResponse: HttpErrorResponse) {
    this.sessionErrorsHandledByInterceptor.add(httpErrorResponse);
  }

  private errorHandler(errorType: HandledHttpErrorTypes, requestedUrl: string) {
    captureException(getHttpRequestException(errorType, requestedUrl), {
      tags: {
        type: errorType
      }
    });

    this.notificationService.error(`ERROR.GLOBAL.${errorType.toUpperCase()}`);
  }
}

const getHttpRequestException = (
  httpErrorResponse: HandledHttpErrorTypes,
  msg: string
) => {
  switch (httpErrorResponse) {
    case 'bad_request':
      return new BadRequest(msg);
    case 'internal_server_error':
      return new InternalServerError(msg);
    case 'forbidden':
      return new Forbidden(msg);
    case 'no_response':
      return new NoResponse(msg);
    case 'not_found':
      return new NotFound(msg);
    case 'offline':
      return new Offline(msg);
    case 'service_unavailable':
      return new ServiceUnavailable(msg);
    case 'unauthenticated':
      return new Unauthenticated(msg);
    case 'unprocessable_entity':
      return new UnprocessableEntity(msg);
    case 'unknown':
      return new Unknown(msg);
  }
};

// translations
_('ERROR.GLOBAL.UNKNOWN');
_('ERROR.GLOBAL.BAD_REQUEST');
_('ERROR.GLOBAL.INTERNAL_SERVER_ERROR');
_('ERROR.GLOBAL.UNAUTHENTICATED');
_('ERROR.GLOBAL.FORBIDDEN');
_('ERROR.GLOBAL.NOT_FOUND');
_('ERROR.GLOBAL.SERVICE_UNAVAILABLE');
_('ERROR.GLOBAL.NO_RESPONSE');
_('ERROR.GLOBAL.OFFLINE');
_('ERROR.GLOBAL.UNPROCESSABLE_ENTITY');
_('ERROR.PAYMENT.ALREADY_PAID');
_('ERROR.PAYMENT.NOT_AWAITING_PAYMENT');
